import { businessEntityKeys, queryClient } from '@commenda/api';
import { useToggle } from '@commenda/kakashi';
import mixpanel, {
  MIXPANEL_SOURCE,
  MIXPANEL_SURFACE_AREA,
} from '@commenda/mixpanel';
import { IBusinessEntity } from '@commenda/types';
import {
  ACCOUNTING_BASIS_ENUM_TO_TEXT,
  copyToClipboard,
  TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT,
} from '@commenda/utils';
import { useNotification } from '../../../../hooks/useNotification';
import { DataPoint } from '../../../common/DataPoint';
import { shouldShowTaxClassification } from '../../../utils';
import EditAccountingBasisModal from './Modals/EditAccountingBasisModal';
import { EditTaxClassificationModal } from './Modals/EditTaxClassificationModal';

export const TaxInformation = ({
  businessEntity,
  hideEditIconForFields = {
    accountingBasis: false,
    taxClassification: false,
  },
  hideCopyIconForFields = {
    accountingBasis: false,
    taxClassification: false,
  },
  hideDescription = false,
  disabled = false,
}: {
  businessEntity: IBusinessEntity;
  hideEditIconForFields?: {
    accountingBasis?: boolean;
    taxClassification?: boolean;
  };
  hideCopyIconForFields?: {
    accountingBasis?: boolean;
    taxClassification?: boolean;
  };
  hideDescription?: boolean;
  disabled?: boolean;
}) => {
  const editAccountingBasisModalVisibility = useToggle();
  const editTaxClassificationModalVisibility = useToggle();

  const { setNotification } = useNotification();

  return (
    <div className="flex flex-col gap-3xl">
      <div className="flex flex-col gap-xs">
        <h3 className="txt-large-bold">Entity Tax Details</h3>
        {!hideDescription && (
          <p className="txt-small-regular text-text-tertiary">
            Tax-related identifiers as they appear on official tax documents
          </p>
        )}
      </div>
      <EditAccountingBasisModal
        isOpen={editAccountingBasisModalVisibility.visible}
        onClose={editAccountingBasisModalVisibility.hide}
        businessEntity={businessEntity}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: [
              businessEntityKeys.FETCH_BUSINESS_ENTITY,
              +businessEntity.id,
            ],
          });

          mixpanel.track('ENTITY_TAX_DETAILS_ADDED', {
            source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
            surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
            business_entity_id: businessEntity.id,
            company_id: businessEntity.generalCorporationInfo.companyId,
            field: 'accounting_basis',
          });
        }}
      />
      <EditTaxClassificationModal
        isOpen={editTaxClassificationModalVisibility.visible}
        onClose={editTaxClassificationModalVisibility.hide}
        businessEntity={businessEntity}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: [
              businessEntityKeys.FETCH_BUSINESS_ENTITY,
              +businessEntity.id,
            ],
          });

          mixpanel.track('ENTITY_TAX_DETAILS_ADDED', {
            source: MIXPANEL_SOURCE.ENTITY_MANAGEMENT,
            surface_area: MIXPANEL_SURFACE_AREA.ENTITY_MANAGEMENT,
            business_entity_id: businessEntity.id,
            company_id: businessEntity.generalCorporationInfo.companyId,
            field: 'tax_classification',
          });
        }}
      />
      <div className="rounded-xl overflow-clip divide-y divide-border-primary">
        <DataPoint
          title="Accounting Method"
          disabled={disabled}
          value={
            businessEntity.accountingInfo.accountingBasis
              ? ACCOUNTING_BASIS_ENUM_TO_TEXT[
                  businessEntity.accountingInfo.accountingBasis
                ]
              : null
          }
          editConfig={
            !hideEditIconForFields.accountingBasis
              ? {
                  isAllowed: true,
                  onEdit: editAccountingBasisModalVisibility.show,
                }
              : undefined
          }
          onCopy={
            !hideCopyIconForFields.accountingBasis
              ? () => {
                  copyToClipboard(
                    businessEntity.accountingInfo.accountingBasis
                      ? ACCOUNTING_BASIS_ENUM_TO_TEXT[
                          businessEntity.accountingInfo.accountingBasis
                        ]
                      : ''
                  );

                  setNotification({
                    show: true,
                    valid: true,
                    title: 'Copied to clipboard',
                    message: 'Accounting method copied to clipboard',
                  });
                }
              : undefined
          }
        />
        {shouldShowTaxClassification({
          incorporationCountry:
            businessEntity.generalCorporationInfo.incorporationCountry,
        }) ? (
          <DataPoint
            title="Tax classification"
            disabled={disabled}
            value={
              businessEntity?.onboardingInfo?.countrySpecificFields?.US?.taxedAs
                ? TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT[
                    businessEntity?.onboardingInfo?.countrySpecificFields?.US
                      ?.taxedAs
                  ]
                : null
            }
            editConfig={
              !hideEditIconForFields.taxClassification
                ? {
                    isAllowed: true,
                    onEdit: editTaxClassificationModalVisibility.show,
                  }
                : undefined
            }
            onCopy={
              !hideCopyIconForFields.taxClassification
                ? () => {
                    copyToClipboard(
                      businessEntity?.onboardingInfo?.countrySpecificFields?.US
                        ?.taxedAs
                        ? TAX_CLASSIFICATION_ENTITY_TYPE_ENUM_TO_TEXT[
                            businessEntity?.onboardingInfo
                              ?.countrySpecificFields?.US?.taxedAs
                          ]
                        : ''
                    );
                  }
                : undefined
            }
          />
        ) : null}
      </div>
    </div>
  );
};
